import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnChanges,
  SimpleChanges,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ColorPickerModule } from '@iplab/ngx-color-picker';
import { TextEditorComponent, TextEditorStyle } from '../../../../../shared/components/text-editor';

export interface BookingData {
  title: string;
  subtitle: string;
  titleStyle?: TextEditorStyle;
  formTheme?: {
    labelColor?: string;
    inputBorderColor?: string;
    inputBackgroundColor?: string;
    inputTextColor?: string;
  };
  button?: {
    text?: string;
    backgroundColor?: string;
    textColor?: string;
  };
  backgroundColor?: string;
}

/**
 * MinimalBookingPanelComponent - a compact editor panel for configuring a booking panel's content, colors and form theme.
 * Provides UI controls to update title, subtitle, form styling (light/dark), button appearance and background colors,
 * and emits changes back to the parent. Supports a small set of predefined colors plus one custom color via a color picker.
 * Key behaviors: maintains active tab (content/layout), expandable/collapsible sections, theme selection, and color picker state.
 * Lifecycle:
 *   - ngOnInit: ensures default data shape and theme detection, initializes custom colors from provided data.
 *   - ngOnChanges: watches for input data changes to reload custom colors when backgroundColor changes.
 * Key methods:
 *   - onClose(): emits closePanel to request the panel be closed.
 *   - switchTab(tab): switch between 'content' and 'layout' editing views.
 *   - toggleSection(section) / isSectionExpanded(section): manage which accordion-style section is expanded.
 *   - onHeadingChange(value), onHeadingStyleChange(style), onSubtitleChange(value): update text fields and emit changes.
 *   - onFormThemeChange(), onButtonChange(), onBackgroundColorChange(color): apply edits to respective parts of data and emit.
 *   - onAddColor(), openColorPicker(color?), closeColorPicker(), onColorPickerChange(): manage color picker flow, preview and commit.
 *   - selectTheme(theme): quickly apply light or dark form themes and emit changes.
 *   - private loadCustomColorFromData(), addCustomColor(color), emitChange(): internal helpers to manage custom color list and emit cloned data via dataChange.
 * State highlights:
 *   - inputs include a BookingData object with title, subtitle, formTheme, button and backgroundColor; defaults are applied when missing.
 *   - emits dataChange with an updated BookingData when edits happen, and closePanel when the panel should be closed.
 * @component
 * @selector app-minimal-booking-panel
 * @example
 *   <app-minimal-booking-panel [data]="bookingData" (dataChange)="onDataChange($event)" (closePanel)="onClose()"></app-minimal-booking-panel>
 * @input {BookingData} data - Initial booking panel configuration (title, subtitle, formTheme, button, backgroundColor). Defaults are applied for missing fields.
 * @output {EventEmitter<BookingData>} dataChange - Emits a cloned BookingData object whenever the user updates content, theme, button or background color.
 * @output {EventEmitter<void>} closePanel - Emitted when the panel should be closed (e.g., user clicks close).
 */
@Component({
  selector: 'app-minimal-booking-panel',
  standalone: true,
  imports: [CommonModule, FormsModule, ColorPickerModule, TextEditorComponent],
  templateUrl: './booking-panel.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./booking-panel.component.scss'],
})
export class MinimalBookingPanelComponent implements OnInit, OnChanges {
  @Input() data: BookingData = {
    title: '',
    subtitle: '',
    formTheme: {
      labelColor: '#555963',
      inputBorderColor: '#E9EAEC',
      inputBackgroundColor: '#FFFFFF',
      inputTextColor: '#15172E',
    },
    button: {
      text: 'Book Appointment',
      backgroundColor: '#1B5BFF',
      textColor: '#FFFFFF',
    },
    backgroundColor: '#FFFFFF',
  };

  @Output() dataChange = new EventEmitter<BookingData>();
  @Output() closePanel = new EventEmitter<void>();

  activeTab: 'content' | 'layout' = 'content';
  expandedSections: Record<string, boolean> = {
    heading: true,
    formTheme: false,
    button: false,
    background: false,
  };
  selectedTheme: 'light' | 'dark' = 'light';

  customColors: string[] = [];

  // Color picker state (same as hero panel)
  isColorPickerOpen = false;
  currentPickerColor: string | any = '#000000';

  private static readonly PREDEFINED_COLORS = ['#5E19A1', '#FF9736', '#079271', '#1B5BFF'];

  /**
   * Initialize component state: ensure default theme and button settings, determine selectedTheme, and load custom color from data.
   * @example
   * gOnInit()
   * undefined
   * @returns {{void}} No return value; initializes component data and state.
   **/
  ngOnInit(): void {
    // Ensure default values are set
    if (!this.data.formTheme) {
      this.data.formTheme = {
        labelColor: '#555963',
        inputBorderColor: '#E9EAEC',
        inputBackgroundColor: '#FFFFFF',
        inputTextColor: '#15172E',
      };
      this.selectedTheme = 'light';
    } else {
      // Determine theme based on existing formTheme values
      if (
        this.data.formTheme.inputBackgroundColor === '#1C1E21' ||
        this.data.formTheme.inputBackgroundColor === '#1c1e21'
      ) {
        this.selectedTheme = 'dark';
      } else {
        this.selectedTheme = 'light';
      }
    }
    if (!this.data.button) {
      this.data.button = {
        text: 'Book Appointment',
        backgroundColor: '#1B5BFF',
        textColor: '#FFFFFF',
      };
    }

    // Load custom color on initial load
    this.loadCustomColorFromData();
  }

  // Lifecycle hook to detect when data input changes
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['data'] && this.data?.backgroundColor) {
      // Load custom color if backgroundColor is not in predefined colors
      this.loadCustomColorFromData();
    }
  }

  // Load custom color from data if it's not a predefined color
  /**
   * Loads a custom background color from the component's data and updates the customColors array accordingly.
   * @example
   * loadCustomColorFromData()
   * // undefined
   * @returns {void} Does not return a value.
   */
  private loadCustomColorFromData(): void {
    if (!this.data?.backgroundColor) {
      return;
    }

    const bgColor = this.data.backgroundColor;

    // Check if the color is not in predefined colors
    if (!MinimalBookingPanelComponent.PREDEFINED_COLORS.includes(bgColor)) {
      // Add to custom colors (maximum 1 custom color)
      this.customColors = [bgColor];
    } else {
      // If it's a predefined color, clear custom colors
      this.customColors = [];
    }
  }

  onClose(): void {
    this.closePanel.emit();
  }

  switchTab(tab: 'content' | 'layout'): void {
    this.activeTab = tab;
  }

  /**
   * Toggle expansion state for a given section; if expanding, collapse all other sections first.
   * @example
   * oggleSection('details')
   * undefined
   * @param {string} section - The key identifying the section to toggle.
   * @returns {void} No return value; mutates this.expandedSections to reflect the new state.
   */
  toggleSection(section: string): void {
    const wasExpanded = this.expandedSections[section] || false;

    if (wasExpanded) {
      // If clicking an expanded section, just collapse it
      this.expandedSections[section] = false;
    } else {
      // If clicking a collapsed section, close all others first, then expand this one
      Object.keys(this.expandedSections).forEach(key => {
        this.expandedSections[key] = false;
      });
      this.expandedSections[section] = true;
    }
  }

  isSectionExpanded(section: string): boolean {
    return this.expandedSections[section] || false;
  }

  onHeadingChange(value: string): void {
    this.data.title = value;
    this.emitChange();
  }

  onHeadingStyleChange(style: TextEditorStyle): void {
    this.data.titleStyle = style;
    this.emitChange();
  }

  onSubtitleChange(value: string): void {
    this.data.subtitle = value;
    this.emitChange();
  }

  onFormThemeChange(): void {
    this.emitChange();
  }

  onButtonChange(): void {
    this.emitChange();
  }

  onBackgroundColorChange(color: string): void {
    this.data.backgroundColor = color;
    this.emitChange();
  }

  onAddColor(): void {
    // Open color picker modal (same as hero panel)
    this.openColorPicker();
  }

  // Open color picker for custom color
  openColorPicker(color?: string): void {
    this.currentPickerColor = color || this.data.backgroundColor || '#000000';
    this.isColorPickerOpen = true;
  }

  // Close color picker and add the selected color to custom colors list
  /**
   * Handle closing the color picker: if a color is selected apply it to the background, add it to custom colors (replacing the single custom color), and close the picker.
   * @example
   * loseColorPicker()
   * undefined
   * @param {{void}} {{none}} - No parameters.
   * @returns {{void}} No return value; performs side effects (applies color, updates custom colors, closes picker).
   **/
  closeColorPicker(): void {
    if (this.currentPickerColor) {
      // Extract hex color from color object (chrome-picker returns an object with hex property)
      let hexColor: string;
      if (typeof this.currentPickerColor === 'string') {
        hexColor = this.currentPickerColor;
      } else if (
        this.currentPickerColor &&
        typeof this.currentPickerColor === 'object' &&
        'hex' in this.currentPickerColor
      ) {
        hexColor = (this.currentPickerColor as any).hex;
      } else {
        hexColor = String(this.currentPickerColor);
      }

      // Ensure hex color starts with #
      if (!hexColor.startsWith('#')) {
        hexColor = '#' + hexColor;
      }

      // Apply the color immediately
      this.onBackgroundColorChange(hexColor);

      // Add to custom colors (maximum 1 custom color - replace if exists)
      this.addCustomColor(hexColor);
    }
    this.isColorPickerOpen = false;
  }

  // Handle color change from color picker (update preview only, don't add to list yet)
  /**
   * Normalize the current color picker value to a hex string (ensuring a leading '#'), store it back to this.currentPickerColor, and apply it immediately as a preview via onBackgroundColorChange without adding it to custom colors.
   * @example
   * nColorPickerChange()
   * undefined
   * @returns {void} No return value; updates component state and triggers a preview color change.
   */
  onColorPickerChange(): void {
    if (this.currentPickerColor) {
      // Extract hex color from color object (chrome-picker returns an object with hex property)
      let hexColor: string;
      if (typeof this.currentPickerColor === 'string') {
        hexColor = this.currentPickerColor;
      } else if (
        this.currentPickerColor &&
        typeof this.currentPickerColor === 'object' &&
        'hex' in this.currentPickerColor
      ) {
        hexColor = (this.currentPickerColor as any).hex;
      } else {
        hexColor = String(this.currentPickerColor);
      }

      // Ensure hex color starts with #
      if (!hexColor.startsWith('#')) {
        hexColor = '#' + hexColor;
      }

      // Update currentPickerColor to the hex string for consistency
      this.currentPickerColor = hexColor;

      // Apply the color immediately for preview (but don't add to custom colors list yet)
      this.onBackgroundColorChange(hexColor);
    }
  }

  private addCustomColor(color: string): void {
    // Maximum 1 custom color - replace existing if any
    if (!MinimalBookingPanelComponent.PREDEFINED_COLORS.includes(color)) {
      // Clear existing custom colors and add the new one
      this.customColors = [color];
    }
  }

  /**
   * Apply the specified 'light' or 'dark' theme to the booking form and emit a change notification.
   * @example
   * electTheme('light')
   * undefined
   * @param {{'light' | 'dark'}} {{theme}} - Theme to apply; either 'light' or 'dark'.
   * @returns {{void}} No return value.
   **/
  selectTheme(theme: 'light' | 'dark'): void {
    this.selectedTheme = theme;
    if (theme === 'light') {
      this.data.formTheme = {
        labelColor: '#555963',
        inputBorderColor: '#E9EAEC',
        inputBackgroundColor: '#FFFFFF',
        inputTextColor: '#15172E',
      };
    } else {
      this.data.formTheme = {
        labelColor: '#FFFFFF',
        inputBorderColor: '#3A3D42',
        inputBackgroundColor: '#1C1E21',
        inputTextColor: '#FFFFFF',
      };
    }
    this.emitChange();
  }

  private emitChange(): void {
    this.dataChange.emit({ ...this.data });
  }

  trackByIndex(index: number): number {
    return index;
  }
  trackByValue(_: number, item: any): any {
    return item?.value ?? item;
  }
  trackById(_: number, item: any): any {
    return item?.id ?? item;
  }
  trackByString(_: number, s: any): any {
    return s;
  }
}
